import React, { useEffect, useState, useContext } from 'react'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { useNavigate } from 'react-router-dom'
import { getPriorityColor } from '../../../utils/Helper'
import { RiUserFollowLine } from 'react-icons/ri'
import Select from 'react-select'
import { getRole } from '../../../utils/cookies/cookies'
import { ProfileContext } from '../../../context/profile'

const FollowUp = ({ reload,highlightTaskId }) => {
  const navigate = useNavigate()
  const role = getRole()
  const { profileData } = useContext(ProfileContext)

  const [tasksList, setTaskList] = useState([])
  const [taskStatus, setTaskStatus] = useState([])
  const [bigTaskTypes, setBigTaskTypes] = useState([])
  const [bigTaskStatuses, setBigTaskStatuses] = useState([])
  const [selectedStatus, setSelectedStatus] = useState({})
  const [selectedBigTaskStatus, setSelectedBigTaskStatus] = useState({})
  const [taskTimelineMap, setTaskTimelineMap] = useState({})

  const fetchData = async () => {
    try {
      const response = await axiosAuthInstance.get('task/list', {
        params: {
          followup: true,
          sortPriority: true,
        },
      })

      if (response?.status === 200) {
        setTaskList(response.data?.result?.docs || [])
      }
    } catch (error) {
      console.error('Error fetching tasks', error)
    }
  }

  const fetchBigTaskType = async () => {
    try {
      const response = await axiosAuthInstance.get('big-task-type/list', {
        params: {
          page: 1,
          limit: 50,
          search: '',
        },
      })

      if (response?.status === 200) {
        const result = response.data?.result?.docs || []
        const options = result.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setBigTaskTypes(options)
      }
    } catch (error) {
      console.error('Error fetching big task type:', error)
    }
  }
  const fetchBigTaskStatus = async () => {
    try {
      const response = await axiosAuthInstance.get('big-task-status/list', {
        params: {
          page: 1,
          limit: 50,
          search: '',
        },
      })

      if (response?.status === 200) {
        const result = response.data?.result?.docs || []
        const options = result.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setBigTaskStatuses(options)
      }
    } catch (error) {
      console.error('Error fetching big task type:', error)
    }
  }

  const fetchTaskStatus = async () => {
    try {
      const response = await axiosAuthInstance.get('task-status/dropdown')
      if (response?.status === 200) {
        const transformedData = response.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setTaskStatus(transformedData)
      }
    } catch (error) {
      console.error('Error fetching task status', error)
    }
  }

  const handleStatusChange = async (selectedOption, taskId) => {
    setSelectedStatus((prev) => ({
      ...prev,
      [taskId]: selectedOption?.value,
    }))
    try {
      await axiosAuthInstance.put(`task/status/${taskId}`, {
        status: selectedOption?.value,
        isCompleted: selectedOption?.label === 'Final Submit',
      })
      if (typeof reload === 'function') {
        reload()
      }
      fetchData()
    } catch (error) {
      console.error('Error updating task status', error)
    }
  }

  const handleBigTaskStatusChange = async (selectedOption, taskId) => {
    setSelectedBigTaskStatus((prev) => ({
      ...prev,
      [taskId]: selectedOption?.value,
    }))
    try {
      const response = await axiosAuthInstance.put(
        `task/bigtask-status/${taskId}`,
        {
          bigTaskStatus: selectedOption?.value,
          isCompleted: selectedOption?.label === 'Final Submit',
        },
      )
    } catch (error) {
      console.error('Error updating big task status', error)
    }
  }
  const fetchTimelineForTasks = async (tasks = []) => {
    try {
      const timelineMap = {}

      await Promise.all(
        tasks.map(async (task) => {
          const res = await axiosAuthInstance.get(`task-start-end/${task._id}`)

          if (res.status === 200 && res.data?.data?.length > 0) {
            const lastAction =
              res.data.data[res.data.data.length - 1].action
            timelineMap[task._id] = lastAction
          } else {
            timelineMap[task._id] = null
          }
        })
      )

      setTaskTimelineMap(timelineMap)
    } catch (error) {
      console.error('Timeline fetch error:', error)
    }
  }

  const handleTimelineAction = async (e, taskId, action) => {
    e.stopPropagation()

    const addedById = profileData?._id
    if (!addedById) {
      console.error('No user ID found in ProfileContext')
      return
    }

    const payload = {
      task: taskId,
      action,
      addedBy: addedById,
      addedByType: role === 'Admin' ? 'admin' : 'team',
    }

    try {
      const res = await axiosAuthInstance.post('task-start-end/add', payload)

      if (res.status === 200) {
        setTaskTimelineMap((prev) => ({
          ...prev,
          [taskId]: action,
        }))

        // Change status after timeline update
        if (taskStatus.length === 0) {
          console.warn('taskStatus not loaded yet')
          return
        }

        let statusLabel = action === 'start' ? 'START' : 'Completed'
        const newStatus = taskStatus.find((s) => s.label === statusLabel)

        if (newStatus) {
          await handleStatusChange(newStatus, taskId)
        } else {
          console.warn(`Status label "${statusLabel}" not found in taskStatus`)
        }
      }
    } catch (error) {
      console.error('Timeline action failed:', error.response?.data || error)
    }
  }

  useEffect(() => {
    fetchData()
    fetchTaskStatus()
    fetchBigTaskStatus()
    fetchBigTaskType()

  }, [])
  useEffect(() => {
    if (tasksList.length > 0) {
      fetchTimelineForTasks(tasksList)
    }
  }, [tasksList])
  useEffect(() => {

  if (tasksList.length === 0) return;

  const timeout = setTimeout(() => {
    const el = document.getElementById(`task-${highlightTaskId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 300);

  return () => clearTimeout(timeout);
}, [highlightTaskId, tasksList]);


  return (
    <section className='mt-4'>
      <h2 className='text-xl font-semibold mb-4 flex items-center gap-2'>
        <RiUserFollowLine className='h-5 w-5' />
        Follow Up
      </h2>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
        {tasksList?.length > 0 ? (
          tasksList.map((task) => {
            const priorityClass = getPriorityColor(task.priority)
            const isDueDatePassed =
              task.dueDate && new Date(task.dueDate) < new Date()
            const passedDueDate = isDueDatePassed ? 'bg-red-200' : 'bg-white'

            return (
              <div
  key={task._id}
  id={`task-${task._id}`}
  className={`rounded-lg shadow p-4 transition-all ${
    highlightTaskId === task._id
      ? 'ring-2 ring-orange-500 bg-orange-100'
      : passedDueDate
  }`}
>

                <h3
                  className='font-medium mb-2 cursor-pointer truncate'
                  onClick={() => navigate(`/task/view/${task._id}`)}
                  title={task.name}
                >
                  {task.name}
                </h3>
                <div className='flex items-center mb-1'>
                  <span
                    className={`px-2 text-sm py-1 rounded-full ${priorityClass}`}
                  >
                    {task.priority}
                  </span>
                  <span className='ml-4 truncate'>{task.assignTo}</span>
                </div>

                <div class="flex justify-between items-center">
                  <div className='mt-1 text-md'>
                    <strong>Status:</strong> {task.status || 'Not Started Yet'}
                  </div>
                  {task.status === 'NOT STARTED' || task.status === 'Reopened' ? (
                    <button
                      onClick={(e) => handleTimelineAction(e, task._id, 'start')}
                      className='bg-green-500 text-white px-3 py-1 rounded mt-2'
                    >
                      Start
                    </button>
                  ) : (
                    <button
                      onClick={(e) => handleTimelineAction(e, task._id, 'end')}
                      className='bg-red-500 text-white px-3 py-1 rounded mt-2'
                    >
                      End
                    </button>
                  )}


                </div>
                {/* <div className="flex items-center justify-between mt-2">
  <div className="text-sm font-medium text-gray-700">
    <span className="">Status:</span>{' '}
    <span className="uppercase text-gray-900">
      {task.status || 'NOT STARTED'}
    </span>
  </div>

  {taskTimelineMap[task._id] !== 'start' ? (
    <button
      onClick={(e) => handleTimelineAction(e, task._id, 'start')}
      className="bg-green-500 hover:bg-green-600 text-white px-4 py-1 rounded text-sm transition"
    >
      Start
    </button>
  ) : (
    <button
      onClick={(e) => handleTimelineAction(e, task._id, 'end')}
      className="bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded text-sm transition"
    >
      End
    </button>
  )}
</div> */}


                <Select
                  className='mt-2'
                  value={taskStatus.find(
                    (opt) => opt.value === selectedStatus[task._id],
                  )}
                  onChange={(opt) => handleStatusChange(opt, task._id)}
                  options={taskStatus}
                  placeholder='Change Status'
                />
                {task.bigButton === true && (
                  <div>
                    {/* Big Task Type display */}
                    <div className='mt-3'>
                      <strong>Big Task Type:</strong>{' '}
                      {bigTaskTypes.find((opt) => opt.value === task.bigTaskType)
                        ?.label || 'Not Assigned'}
                    </div>

                    {/* Big Task Status */}
                    <div className='mt-3'>
                      <strong>Big Task Status:</strong>{' '}
                      {bigTaskStatuses.find(
                        (opt) => opt.label === task.bigTaskStatus,
                      )?.label || 'Not Assigned'}
                    </div>
                    <Select
                      className='mt-2'
                      value={
                        task?.bigTaskStatus
                          ? bigTaskStatuses.find(
                            (opt) =>
                              opt.value === selectedBigTaskStatus[task._id],
                          )
                          : null
                      }
                      onChange={(opt) => handleBigTaskStatusChange(opt, task._id)}
                      options={bigTaskStatuses}
                      placeholder='Change Big Task Status'
                    />
                  </div>
                )}
              </div>
            )
          })
        ) : (
          <p>No Tasks</p>
        )}
      </div>
    </section>
  )
}

export default FollowUp
